import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchGenerationStatus } from '@/lib/api-client';
import type { GenerationStatus } from '@/lib/api-client';

const POLL_INTERVAL_MS = 2000;
const TERMINAL_STATES = new Set(['completed', 'failed', 'cancelled']);

export function useGenerationStatus(options: { projectId: string; enabled: boolean }): {
  generation: GenerationStatus | null;
  isPolling: boolean;
  error: string | null;
} {
  const { projectId, enabled } = options;
  const [generation, setGeneration] = useState<GenerationStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const stoppedRef = useRef(false);

  const poll = useCallback(async () => {
    try {
      const result = await fetchGenerationStatus(projectId);
      if (stoppedRef.current) return;

      setGeneration(result.generation);
      setError(null);

      if (result.generation && TERMINAL_STATES.has(result.generation.status)) {
        clearInterval(intervalRef.current);
        setIsPolling(false);
      }
    } catch (err) {
      if (stoppedRef.current) return;
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, [projectId]);

  useEffect(() => {
    if (!enabled) {
      clearInterval(intervalRef.current);
      setIsPolling(false);
      stoppedRef.current = true;
      return;
    }

    stoppedRef.current = false;
    setIsPolling(true);

    // Initial fetch
    poll();

    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      stoppedRef.current = true;
      clearInterval(intervalRef.current);
      setIsPolling(false);
    };
  }, [enabled, poll]);

  return { generation, isPolling, error };
}
